import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || null;
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const validateEmail = (val) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!val || val.trim() === "") {
      return "Email address is required";
    } else if (!emailRegex.test(val.trim())) {
      return "Please enter a valid email address (e.g. name@domain.com)";
    }
    return "";
  };

  const handleEmailChange = (val) => {
    setF((prev) => ({ ...prev, email: val }));
    if (emailTouched) {
      setEmailError(validateEmail(val));
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    setEmailError(validateEmail(f.email));
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validateEmail(f.email);
    setEmailTouched(true);
    if (err) {
      setEmailError(err);
      toast.error(err);
      return;
    }
    setBusy(true);
    const r = await login(f.email, f.password);
    setBusy(false);
    if (r.ok) {
      toast.success("Welcome back.");
      if (next) return nav(next);
      if (r.user.role === "master_admin" || r.user.role === "branch_admin") nav("/admin");
      else nav("/dashboard");
    } else {
      toast.error("server is crashed");
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-hblack" data-testid="login-page">
      <div className="relative hidden lg:block">
        <img src="https://images.pexels.com/photos/38045116/pexels-photo-38045116.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/80 to-black/40" />
        <motion.div
          initial={{ opacity: 0, x: -100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
          className="relative h-full flex flex-col justify-end p-12"
        >
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-extrabold mb-3">HAMMAM OS PRIVILEGE</div>
          <div className="font-heading text-4xl text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 leading-tight italic font-bold">
            "The best hospitality is invisible until you need it."
          </div>
        </motion.div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <motion.form
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
          onSubmit={submit}
          className="w-full max-w-md p-8 lg:p-10 rounded-2xl bg-gradient-to-b from-hpaper via-black/90 to-hpaper border-2 border-hgold/70 shadow-xl shadow-yellow-950/30 space-y-6"
          data-testid="contact-form"
        >
          <Link to="/" className="text-xs uppercase tracking-widest text-hgold hover:text-amber-300 font-extrabold flex items-center gap-1">
            ← Back to site
          </Link>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-extrabold mb-1">Sign In To Account</div>
            <h1 className="text-3xl font-heading text-white font-bold">Welcome back.</h1>
          </div>

          <div>
            <label className="text-sm font-semibold text-white/90 block mb-1.5">Email Address</label>
            <input
              required
              type="email"
              placeholder="Enter your email address"
              data-testid="login-email"
              value={f.email}
              onChange={(e) => handleEmailChange(e.target.value)}
              onBlur={handleEmailBlur}
              className={`w-full bg-black/70 border ${
                emailError ? "border-red-500 focus:border-red-500" : "border-white/20 focus:border-hgold"
              } px-4 py-3 text-sm text-white outline-none rounded-xl transition-all`}
            />
            {emailError && (
              <span className="text-red-400 text-xs font-semibold mt-1.5 block animate-pulse">
                ⚠️ {emailError}
              </span>
            )}
          </div>

          <div>
            <label className="text-sm font-semibold text-white/90 block mb-1.5">Password</label>
            <div className="relative flex items-center">
              <input
                required
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                data-testid="login-password"
                value={f.password}
                onChange={(e) => setF({ ...f, password: e.target.value })}
                className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 pr-12 text-sm text-white outline-none rounded-xl transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 text-white/60 hover:text-hgold transition-colors p-1 flex items-center justify-center focus:outline-none"
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="gold-btn w-full justify-center flex items-center gap-2 rounded-xl py-3.5 font-extrabold text-xs uppercase tracking-wider shadow-lg shadow-yellow-950/30"
          >
            {busy ? (
              <>
                <div className="premium-spinner animate-spin" style={{ width: "1.25rem", height: "1.25rem", borderWidth: "2px" }}></div>
                <span>Signing in...</span>
              </>
            ) : (
              "Sign in"
            )}
          </button>

          <div className="text-sm text-hsecondary text-center pt-2">
            No account? <Link to="/register" className="text-hgold hover:text-amber-300 font-bold underline">Create one</Link>
          </div>
        </motion.form>
      </div>
    </div>
  );
}
